import ClearIcon from '@mui/icons-material/Clear';
import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { FileUpload, FileUploadProps } from 'retired/FileUpload/FileUpload';

export type FileUploadInputOnClear = () => void;

export type FileUploadInputClearProps = {
  onClear: FileUploadInputOnClear;
};

export type FileUploadInputLinkPreviewProps = {
  required?: boolean;
  onClear: FileUploadInputOnClear;
  value: string;
};

export type FileUploadInputImagePreviewProps = {
  required?: boolean;
  onClear: FileUploadInputOnClear;
  label?: React.ReactNode;
  value: string;
  PassedFileUploadInputClear: (props: FileUploadInputClearProps) => JSX.Element;
};

export type FileUploadInputProps = {
  FileUploadInputClear?: (props: FileUploadInputClearProps) => JSX.Element;
  FileUploadInputLinkPreview?: (props: FileUploadInputLinkPreviewProps) => JSX.Element;
  FileUploadInputImagePreview?: (props: FileUploadInputImagePreviewProps) => JSX.Element;
  name?: string;
  onChange: (fileUri: string) => void;
  imagePreview?: boolean;
  linkPreview?: boolean;
  onClear?: FileUploadInputOnClear;
  value: string;
} & Pick<TextFieldProps, 'required' | 'error' | 'label' | 'helperText'> &
  FileUploadProps;

export const FileUploadInputClear = ({ onClear }: FileUploadInputClearProps): JSX.Element => (
  <Box
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
    }}
  >
    <Tooltip title="Clear">
      <IconButton onClick={onClear}>
        <ClearIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

export const FileUploadInputLinkPreview = ({
  required,
  onClear,
  value,
}: FileUploadInputLinkPreviewProps) => (
  <Box>
    <Typography>
      Your document was uploaded successfully.
      <br />
      <Link href={`${value}?download=false`} target="_blank" rel="noreferrer">
        View your document
      </Link>
      .
    </Typography>
    {!required ? (
      <Typography>
        Not satisfied? {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link component="button" onClick={onClear}>
          <Typography>Remove this document</Typography>
        </Link>
        .
      </Typography>
    ) : null}
  </Box>
);

export const FileUploadInputImagePreview = ({
  required,
  onClear,
  label,
  value,
  PassedFileUploadInputClear,
}: FileUploadInputImagePreviewProps) => (
  <Box
    style={{
      position: 'relative', // @NOTE-RT: 'cause `PassedFileUploadInputClear` has `position: absolute`
    }}
  >
    <Box
      style={{
        height: 250,
        alignContent: 'center',
        textAlign: 'center',
      }}
    >
      <img
        style={{
          objectFit: 'contain',
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: 0,
        }}
        src={value}
        alt={`Uploaded ${label}`}
      />
    </Box>
    {!required && <PassedFileUploadInputClear onClear={onClear} />}
  </Box>
);

export const FileUploadInput = ({
  FileUploadInputClear: PassedFileUploadInputClear = FileUploadInputClear,
  FileUploadInputLinkPreview: PassedFileUploadInputLinkPreview = FileUploadInputLinkPreview,
  FileUploadInputImagePreview: PassedFileUploadInputImagePreview = FileUploadInputImagePreview,
  name,
  required,
  onChange,
  value,
  error,
  label,
  helperText,
  imagePreview = false,
  linkPreview = !imagePreview,
  uploadCta,
  acceptedTypes,
  withCrop,
  cropRatio,
  fixed = false,
  maxSize,
  quality,
  contentDisposition,
  isZoomable = false,
  s3Acl,
  s3Bucket,
  onClear = (): void => onChange(''),
}: FileUploadInputProps): JSX.Element => {
  const actualFileUploadProps = {
    name,
    acceptedTypes,
    withCrop,
    cropRatio,
    fixed,
    maxSize,
    quality,
    contentDisposition,
    isZoomable,
    uploadCta,
    s3Acl,
    s3Bucket,
  };

  return (
    <FormControl>
      <Stack>
        <InputLabel error={error} required={required}>
          {label}
        </InputLabel>
        <Box>
          <Box
            style={{
              height: 250,
              position: 'relative', // @NOTE-RT: 'cause `FileUpload` has `position: absolute`
            }}
          >
            <FileUpload
              {...actualFileUploadProps}
              onFinish={({ publicUrl }) => onChange(publicUrl)}
            />
          </Box>
          {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
        </Box>
        {value && (
          <>
            {imagePreview && (
              <PassedFileUploadInputImagePreview
                label={label}
                value={value}
                required={required}
                onClear={onClear}
                PassedFileUploadInputClear={PassedFileUploadInputClear}
              />
            )}
            {linkPreview && (
              <PassedFileUploadInputLinkPreview
                value={value}
                required={required}
                onClear={onClear}
              />
            )}
          </>
        )}
      </Stack>
    </FormControl>
  );
};
